import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, STATUS_COLORS, mondayOf, isoDay, isoWeek, WEEKDAY_NL, MONTH_NL } from "./shared";

// JACE Archive — afgeronde/verleden sessies, maand → week → dag, nieuwste eerst
export default function JaceArchive() {
  const [sessions, setSessions] = useState(null);
  const [type, setType] = useState("");
  const [coach, setCoach] = useState("");

  useEffect(() => {
    api.get("/jace/sessions").then(({ data }) => setSessions(data.items)).catch(() => setSessions([]));
  }, []);

  const todayIso = isoDay(new Date());
  const archived = useMemo(() => (sessions || []).filter((s) => {
    const done = ["COMPLETED", "REVIEWED"].includes(s.status) || (s.date && s.date < todayIso);
    if (!done) return false;
    if (type && s.type !== type) return false;
    if (coach && s.assigned_coach !== coach && s.programmed_by !== coach) return false;
    return true;
  }).sort((a, b) => (b.date + (b.start_time || "")).localeCompare(a.date + (a.start_time || ""))), [sessions, type, coach, todayIso]);

  const coaches = useMemo(() => [...new Set((sessions || []).map((s) => s.assigned_coach || s.programmed_by).filter(Boolean))], [sessions]);

  // groepeer: maand → week → sessies
  const grouped = useMemo(() => {
    const out = [];
    for (const s of archived) {
      const d = new Date(s.date + "T00:00:00");
      const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const mLabel = `${MONTH_NL[d.getMonth()]} ${d.getFullYear()}`;
      const wKey = isoDay(mondayOf(d));
      let m = out.find((x) => x.key === mKey);
      if (!m) { m = { key: mKey, label: mLabel, weeks: [] }; out.push(m); }
      let w = m.weeks.find((x) => x.key === wKey);
      if (!w) { w = { key: wKey, num: isoWeek(d), sessions: [] }; m.weeks.push(w); }
      w.sessions.push(s);
    }
    return out;
  }, [archived]);

  return (
    <JaceLayout>
      <div data-testid="jace-archive">
        <div className="section-tag section-tag-dark mb-3">// ARCHIVE — WHAT HAVE WE ALREADY DONE?</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <h2 className="font-display uppercase text-4xl md:text-6xl">Archive.</h2>
          <div className="flex gap-2">
            <select value={type} onChange={(e) => setType(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-arch-type">
              <option value="" className="bg-[#0A0A0A]">Alle types</option>
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k} className="bg-[#0A0A0A]">{l}</option>)}
            </select>
            <select value={coach} onChange={(e) => setCoach(e.target.value)} className="editorial-input dark !w-auto" data-testid="jace-arch-coach">
              <option value="" className="bg-[#0A0A0A]">Alle coaches</option>
              {coaches.map((c) => <option key={c} value={c} className="bg-[#0A0A0A]">{c}</option>)}
            </select>
          </div>
        </div>

        {sessions === null ? (
          <div className="chip text-[#A3A3A3]">Laden…</div>
        ) : grouped.length === 0 ? (
          <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#4A4A4A]" data-testid="jace-arch-empty">Nog geen afgeronde sessies in het archief.</div>
        ) : (
          <div data-testid="jace-arch-list">
            {grouped.map((m) => (
              <div key={m.key} className="mb-10">
                <div className="font-display uppercase text-3xl mb-4" style={{ color: JACE }}>{m.label}</div>
                {m.weeks.map((w) => (
                  <div key={w.key} className="mb-6">
                    <div className="chip text-[#A3A3A3] mb-2">WEEK {w.num}</div>
                    <div className="line-t line-dark">
                      {w.sessions.map((s) => {
                        const d = new Date(s.date + "T00:00:00");
                        return (
                          <Link key={s.session_id} to={`/jace/sessions/${s.session_id}`} className="line-b line-dark py-3.5 flex flex-wrap items-center gap-4 hover:bg-white/[0.03] transition-colors" data-testid={`jace-arch-session-${s.session_id}`}>
                            <span className="chip text-[#A3A3A3] w-32 shrink-0">{WEEKDAY_NL[(d.getDay() + 6) % 7].slice(0, 3)} {String(d.getDate()).padStart(2, "0")} {MONTH_NL[d.getMonth()].slice(0, 3)}</span>
                            <span className="chip w-28 shrink-0" style={{ color: JACE }}>{s.session_id}</span>
                            <span className="font-display text-lg uppercase flex-1 min-w-32 truncate">{s.title || "—"}</span>
                            <span className="chip text-[#A3A3A3] hidden md:inline">{TYPE_LABELS[s.type]} · {s.level}</span>
                            <span className="chip px-2 py-0.5 border" style={{ color: STATUS_COLORS[s.status], borderColor: `${STATUS_COLORS[s.status]}55` }}>{s.status}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
